import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import { useToast } from '../components/Toast';
import { getQuestions, getQuestion, createQuestion, answerQuestion, getSubscription } from '../api/client';

const CATEGORIES = ['Налоги', 'Закон и право', 'Маркетинг', 'Расценки', 'Договоры', 'Бухгалтерия', 'Страхование', 'Материалы', 'Инструмент', 'Кадры', 'Гарантия', 'Другое'];
const CAT_ICONS = { 'Налоги': '📊', 'Закон и право': '⚖️', 'Маркетинг': '📈', 'Расценки': '💰', 'Договоры': '📄', 'Бухгалтерия': '🧾', 'Страхование': '🛡️', 'Материалы': '🧱', 'Инструмент': '🔧', 'Кадры': '👷', 'Гарантия': '✅', 'Другое': '💬' };

export default function QuestionPage({ startParam }) {
  const { toast, toastError } = useToast();
  const hasDeepLink = startParam && startParam.startsWith('q') && !isNaN(startParam.slice(1));
  const [screen, setScreen] = useState(hasDeepLink ? 'detail' : 'list');
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');
  const [detail, setDetail] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [answerFiles, setAnswerFiles] = useState([]);
  const [questionFiles, setQuestionFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const qFileRef = useRef(null);
  const aFileRef = useRef(null);
  const [userRole, setUserRole] = useState('user');
  const [filterCat, setFilterCat] = useState('');
  const [lightbox, setLightbox] = useState(null); // { images: [], index: 0 } or null
  const [lbZoom, setLbZoom] = useState(1);
  const lbStartDist = useRef(null);
  const lbSwipeX = useRef(null);

  const openLightbox = (images, index) => { setLightbox({ images, index }); setLbZoom(1); };
  const lbPrev = () => { if (lightbox && lightbox.index > 0) { setLightbox({ ...lightbox, index: lightbox.index - 1 }); setLbZoom(1); } };
  const lbNext = () => { if (lightbox && lightbox.index < lightbox.images.length - 1) { setLightbox({ ...lightbox, index: lightbox.index + 1 }); setLbZoom(1); } };

  const loadQuestions = useCallback(() => {
    const params = filterCat ? `limit=50&category=${encodeURIComponent(filterCat)}` : 'limit=50';
    getQuestions(params).then(d => setQuestions(d.questions || [])).catch(console.error);
  }, [filterCat]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  useEffect(() => {
    getSubscription().then(d => { if (d.role) setUserRole(d.role); }).catch(() => {});
  }, []);

  // Deep link: open specific question (startapp=q{id})
  useEffect(() => {
    if (hasDeepLink) {
      const qId = startParam.slice(1);
      getQuestion(qId).then(q => { setDetail(q); setScreen('detail'); }).catch(e => { console.error('[DeepLink] Failed:', e); setScreen('list'); });
    }
  }, [startParam, hasDeepLink]);

  const isExpert = userRole === 'admin' || userRole === 'expert';

  const openDetail = (id) => {
    getQuestion(id).then(q => { setDetail(q); setScreen('detail'); }).catch(console.error);
  };

  const handleSubmit = async (type) => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('text', text);
      if (category) formData.append('category', category);
      questionFiles.forEach(f => formData.append('photos', f));
      await createQuestion(formData);
      setText('');
      setCategory('');
      setQuestionFiles([]);
      setScreen('success');
      toast('Вопрос отправлен');
    } catch (e) {
      toastError(e.message || 'Не удалось отправить вопрос');
    }
    setSending(false);
  };

  const handleAnswer = async () => {
    if (!answerText.trim() || !detail) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('text', answerText.trim());
      answerFiles.forEach(f => formData.append('files', f));
      await answerQuestion(detail.id, formData);
      setAnswerText('');
      setAnswerFiles([]);
      const q = await getQuestion(detail.id);
      setDetail(q);
      toast('Ответ отправлен');
    } catch (e) {
      toastError(e.message || 'Не удалось отправить ответ');
    }
    setSending(false);
  };

  const goToList = () => {
    setScreen('list');
    setDetail(null);
    loadQuestions();
  };

  return (
    <div>
      <Header title="Вопросы" />
      {/* Hidden file inputs */}
      <input ref={qFileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" style={{ display: 'none' }}
        onChange={e => {
          const newFiles = Array.from(e.target.files || []);
          const tooBig = newFiles.find(f => f.size > 5 * 1024 * 1024);
          if (tooBig) { toastError(`Файл "${tooBig.name}" слишком большой (макс. 5 МБ)`); e.target.value = ''; return; }
          if (newFiles.length) setQuestionFiles(prev => [...prev, ...newFiles].slice(0, 5));
          e.target.value = '';
        }} />
      <input ref={aFileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" style={{ display: 'none' }}
        onChange={e => {
          const newFiles = Array.from(e.target.files || []);
          const tooBig = newFiles.find(f => f.size > 5 * 1024 * 1024);
          if (tooBig) { toastError(`Файл "${tooBig.name}" слишком большой (макс. 5 МБ)`); e.target.value = ''; return; }
          if (newFiles.length) setAnswerFiles(prev => [...prev, ...newFiles].slice(0, 5));
          e.target.value = '';
        }} />

      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* LIST SCREEN */}
        {screen === 'list' && <>
          {/* Ask buttons */}
          <div style={{ display: 'flex', gap: 10, animation: 'fadeInUp 0.3s ease both' }}>
            <div onClick={() => setScreen('community')} style={askBtnStyle('#0A84FF')}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0A84FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Сообществу</span>
            </div>
            <div onClick={() => setScreen('expert')} style={askBtnStyle('var(--accent)')}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Эксперту</span>
            </div>
          </div>

          {/* Category filter */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            <span onClick={() => setFilterCat('')} style={filterChip(!filterCat)}>Все</span>
            {CATEGORIES.map(c => (
              <span key={c} onClick={() => setFilterCat(c)} style={filterChip(filterCat === c)}>
                {CAT_ICONS[c]} {c}
              </span>
            ))}
          </div>

          {/* Questions list */}
          {questions.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)', fontSize: 14 }}>
              Пока нет вопросов
            </div>
          )}
          {questions.map(q => (
            <div key={q.id} onClick={() => openDetail(q.id)} style={{
              background: 'var(--bg-card)', borderRadius: 12, padding: '12px 14px',
              border: '1px solid var(--border)', cursor: 'pointer',
            }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                {q.category && <span style={tagStyle('var(--accent-dim)', 'var(--accent)')}>{CAT_ICONS[q.category]} {q.category}</span>}
                <span style={tagStyle(
                  q.status === 'answered' ? 'rgba(48,209,88,0.12)' : 'rgba(99,99,102,0.12)',
                  q.status === 'answered' ? 'var(--success)' : 'var(--text-tertiary)'
                )}>{q.status === 'answered' ? 'Отвечен' : 'Ждёт ответа'}</span>
                {q.type === 'expert' && <span style={tagStyle('rgba(191,90,242,0.12)', '#BF5AF2')}>Эксперту</span>}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{q.text}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                {q.author_name} · {q.replies_count || 0} ответов
              </div>
            </div>
          ))}
        </>}

        {/* DETAIL SCREEN */}
        {screen === 'detail' && detail && (
          <div style={{ animation: 'fadeInUp 0.3s ease both' }}>
            <button onClick={goToList} style={backBtn}>← Назад</button>

            {/* Question */}
            <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 16, border: '1px solid var(--border)', marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {detail.category && <span style={tagStyle('var(--accent-dim)', 'var(--accent)')}>{CAT_ICONS[detail.category]} {detail.category}</span>}
                <span style={tagStyle(
                  detail.status === 'answered' ? 'rgba(48,209,88,0.12)' : 'rgba(99,99,102,0.12)',
                  detail.status === 'answered' ? 'var(--success)' : 'var(--text-tertiary)'
                )}>{detail.status === 'answered' ? 'Отвечен' : 'Ждёт ответа'}</span>
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{detail.text}</div>
              {detail.photos && detail.photos.length > 0 && (() => {
                const photos = detail.photos.map(p => typeof p === 'string' ? { file_path: p, file_name: p.split('/').pop() } : p);
                const images = photos.filter(p => /\.(jpg|jpeg|png|webp)$/i.test(p.file_path));
                const docs = photos.filter(p => !/\.(jpg|jpeg|png|webp)$/i.test(p.file_path));
                return <>
                  {images.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
                      {images.map((p, i) => (
                        <img key={i} src={p.file_path} alt="" onClick={() => openLightbox(images.map(x => x.file_path), i)} style={{ width: images.length === 1 ? '100%' : 200, height: images.length === 1 ? 'auto' : 150, maxHeight: 300, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--border)', flexShrink: 0, cursor: 'pointer' }} />
                      ))}
                    </div>
                  )}
                  {docs.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {docs.map((p, i) => (
                        <a key={i} href={p.file_path} target="_blank" rel="noreferrer" style={fileChipStyle}>📎 {p.file_name || p.file_path.split('/').pop()}</a>
                      ))}
                    </div>
                  )}
                </>;
              })()}
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 10 }}>
                {detail.author_name} · {new Date(detail.created_at).toLocaleDateString('ru-RU')}
              </div>
            </div>

            {/* Answers */}
            {detail.answers && detail.answers.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                  Ответы ({detail.answers.length})
                </div>
                {detail.answers.map(a => (
                  <div key={a.id} style={{
                    background: 'var(--bg-card)', borderRadius: 12, padding: '12px 14px',
                    border: '1px solid var(--border)', marginBottom: 8,
                    borderLeft: (a.author_role === 'expert' || a.author_role === 'admin') ? '3px solid var(--accent)' : '3px solid var(--border)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{a.author_name}</span>
                      {(a.author_role === 'expert' || a.author_role === 'admin') && (
                        <span style={tagStyle('rgba(191,90,242,0.12)', '#BF5AF2')}>Эксперт</span>
                      )}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a.text}</div>
                    {a.attachments && a.attachments.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {(() => { const ansImages = a.attachments.filter(x => /\.(jpg|jpeg|png|webp)$/i.test(x.file_path)); return a.attachments.map((att, i) => {
                          const isImage = /\.(jpg|jpeg|png|webp)$/i.test(att.file_path);
                          return isImage
                            ? <img key={i} src={att.file_path} alt="" onClick={() => openLightbox(ansImages.map(x => x.file_path), ansImages.indexOf(att))} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
                            : <a key={i} href={att.file_path} target="_blank" rel="noreferrer" style={fileChipStyle}>📎 {att.file_name || att.file_path.split('/').pop()}</a>;
                        }); })()}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                      {new Date(a.created_at).toLocaleDateString('ru-RU')}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {detail.answers && detail.answers.length === 0 && detail.status !== 'answered' && (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)', fontSize: 13 }}>
                Пока нет ответов. {isExpert ? 'Вы можете ответить ниже.' : 'Ожидайте ответа эксперта.'}
              </div>
            )}

            {/* Answer form — visible to everyone */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                {isExpert ? 'Ответить как эксперт' : 'Написать ответ'}
              </div>
              <textarea
                value={answerText}
                onChange={e => setAnswerText(e.target.value.slice(0, 2000))}
                placeholder={isExpert ? 'Напишите экспертный ответ...' : 'Поделитесь своим опытом...'}
                rows={4}
                style={textareaStyle}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 12, color: answerText.length > 1800 ? 'var(--error)' : 'var(--text-tertiary)' }}>{answerText.length}/2000</span>
                {isExpert && <span style={{ fontSize: 11, color: 'var(--accent)' }}>Ваш ответ отметит вопрос как отвеченный</span>}
              </div>
              <div style={attachBtnStyle} onClick={() => aFileRef.current?.click()}>
                📎 {answerFiles.length > 0 ? `Добавить ещё (${answerFiles.length}/5)` : 'Прикрепить файлы'}
              </div>
              {answerFiles.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {answerFiles.map((f, i) => (
                    <span key={i} style={{ ...fileChipStyle, cursor: 'pointer' }} onClick={() => setAnswerFiles(prev => prev.filter((_, j) => j !== i))}>
                      {f.name.length > 20 ? f.name.slice(0, 17) + '...' : f.name} ✕
                    </span>
                  ))}
                </div>
              )}
              <button
                onClick={handleAnswer}
                disabled={!answerText.trim() || sending}
                style={{ ...submitBtn, opacity: (answerText.trim() && !sending) ? 1 : 0.5, marginTop: 10 }}
              >
                {sending ? 'Отправка...' : 'Отправить ответ'}
              </button>
            </div>
          </div>
        )}

        {/* COMMUNITY FORM */}
        {screen === 'community' && (
          <div style={{ animation: 'fadeInUp 0.3s ease both' }}>
            <button onClick={goToList} style={backBtn}>← Назад</button>
            <div style={{ background: 'rgba(10,132,255,0.08)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0A84FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              <span style={{ fontSize: 13, color: 'var(--blue)' }}>Вопрос сообществу</span>
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Выберите тему</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
              {CATEGORIES.map(c => (
                <div key={c} onClick={() => setCategory(c)} style={{
                  background: category === c ? 'rgba(10,132,255,0.08)' : 'var(--bg-card)',
                  border: '1px solid ' + (category === c ? 'var(--blue)' : 'var(--border)'),
                  borderRadius: 10, padding: '10px', textAlign: 'center', cursor: 'pointer',
                }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{CAT_ICONS[c]}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: category === c ? 'var(--blue)' : 'var(--text-secondary)' }}>{c}</div>
                </div>
              ))}
            </div>

            <textarea value={text} onChange={e => setText(e.target.value.slice(0, 500))} placeholder="Опишите ваш вопрос..." rows={5} style={textareaStyle} autoFocus />
            <div style={{ textAlign: 'right', fontSize: 12, color: text.length > 450 ? 'var(--error)' : 'var(--text-tertiary)', marginTop: 4 }}>{text.length}/500</div>
            <div style={attachBtnStyle} onClick={() => qFileRef.current?.click()}>
              📎 {questionFiles.length > 0 ? `Добавить ещё (${questionFiles.length}/5)` : 'Прикрепить фото / файлы'}
            </div>
            {questionFiles.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {questionFiles.map((f, i) => (
                  <span key={i} style={{ ...fileChipStyle, cursor: 'pointer' }} onClick={() => setQuestionFiles(prev => prev.filter((_, j) => j !== i))}>
                    {f.name.length > 20 ? f.name.slice(0, 17) + '...' : f.name} ✕
                  </span>
                ))}
              </div>
            )}
            <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '10px 14px', marginTop: 12, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Вопрос появится в чате клуба. Участники ответят из своего опыта.</div>
            </div>
            <button onClick={() => handleSubmit('community')} disabled={!text.trim() || sending} style={{ ...submitBtn, opacity: (text.trim() && !sending) ? 1 : 0.5 }}>
              {sending ? 'Отправка...' : 'Отправить'}
            </button>
          </div>
        )}

        {/* EXPERT FORM */}
        {screen === 'expert' && (
          <div style={{ animation: 'fadeInUp 0.3s ease both' }}>
            <button onClick={goToList} style={backBtn}>← Назад</button>
            <div style={{ background: 'var(--accent-dim)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span style={{ fontSize: 13, color: 'var(--accent)' }}>Вопрос эксперту</span>
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Выберите категорию</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
              {CATEGORIES.map(c => (
                <div key={c} onClick={() => setCategory(c)} style={{
                  background: category === c ? 'var(--accent-dim)' : 'var(--bg-card)',
                  border: '1px solid ' + (category === c ? 'var(--accent)' : 'var(--border)'),
                  borderRadius: 10, padding: '10px', textAlign: 'center', cursor: 'pointer',
                }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{CAT_ICONS[c]}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: category === c ? 'var(--accent)' : 'var(--text-secondary)' }}>{c}</div>
                </div>
              ))}
            </div>

            <textarea value={text} onChange={e => setText(e.target.value.slice(0, 1000))} placeholder="Опишите ваш вопрос подробно..." rows={5} style={textareaStyle} />
            <div style={{ textAlign: 'right', fontSize: 12, color: text.length > 900 ? 'var(--error)' : 'var(--text-tertiary)', marginTop: 4 }}>{text.length}/1000</div>
            <div style={attachBtnStyle} onClick={() => qFileRef.current?.click()}>
              📎 {questionFiles.length > 0 ? `Добавить ещё (${questionFiles.length}/5)` : 'Прикрепить фото / файлы'}
            </div>
            {questionFiles.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {questionFiles.map((f, i) => (
                  <span key={i} style={{ ...fileChipStyle, cursor: 'pointer' }} onClick={() => setQuestionFiles(prev => prev.filter((_, j) => j !== i))}>
                    {f.name.length > 20 ? f.name.slice(0, 17) + '...' : f.name} ✕
                  </span>
                ))}
              </div>
            )}
            <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '10px 14px', marginTop: 12, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Эксперт получит уведомление и ответит в течение 24 часов.</div>
            </div>
            <button onClick={() => handleSubmit('expert')} disabled={!text.trim() || !category || sending} style={{ ...submitBtn, opacity: (text.trim() && category && !sending) ? 1 : 0.5 }}>
              {sending ? 'Отправка...' : 'Отправить'}
            </button>
          </div>
        )}

        {/* SUCCESS */}
        {screen === 'success' && (
          <div style={{ textAlign: 'center', padding: '48px 0', animation: 'fadeInUp 0.3s ease both' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(48,209,88,0.15)', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 32, animation: 'popIn 0.4s ease 0.2s both' }}>✓</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Вопрос отправлен!</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>Вы получите уведомление, когда появится ответ</div>
            <button onClick={goToList} style={{ ...submitBtn, background: 'var(--bg-card)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
              Все вопросы
            </button>
            <button onClick={() => { setText(''); setCategory(''); setScreen('list'); }} style={{ background: 'none', color: 'var(--text-secondary)', border: 'none', fontSize: 14, cursor: 'pointer', marginTop: 12 }}>
              Задать ещё вопрос
            </button>
          </div>
        )}
      </div>

      {/* Lightbox gallery */}
      {lightbox && (
        <div onClick={() => { setLightbox(null); setLbZoom(1); }}
          onTouchStart={e => {
            if (e.touches.length === 1) { lbSwipeX.current = e.touches[0].clientX; }
            if (e.touches.length === 2) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; lbStartDist.current = Math.hypot(dx, dy); lbSwipeX.current = null; }
          }}
          onTouchMove={e => {
            if (e.touches.length === 2 && lbStartDist.current) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; const dist = Math.hypot(dx, dy); const scale = dist / lbStartDist.current; setLbZoom(z => Math.min(5, Math.max(0.5, z * (scale > 1 ? 1.02 : 0.98)))); lbStartDist.current = dist; e.preventDefault(); }
          }}
          onTouchEnd={e => {
            if (lbSwipeX.current !== null && e.changedTouches.length === 1 && lbZoom <= 1) {
              const diff = e.changedTouches[0].clientX - lbSwipeX.current;
              if (diff > 60) lbPrev();
              else if (diff < -60) lbNext();
            }
            lbSwipeX.current = null; lbStartDist.current = null;
          }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, cursor: 'pointer', touchAction: 'none',
          }}>
          {/* Close */}
          <button onClick={e => { e.stopPropagation(); setLightbox(null); setLbZoom(1); }} style={{
            position: 'absolute', top: 16, right: 16, background: 'rgba(60,60,60,0.8)',
            border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18,
            color: '#FFCC00', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 2, fontWeight: 700,
          }}>✕</button>

          {/* Counter */}
          {lightbox.images.length > 1 && (
            <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#FFCC00', fontSize: 14, fontWeight: 700 }}>
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}

          {/* Left arrow */}
          {lightbox.images.length > 1 && lightbox.index > 0 && (
            <button onClick={e => { e.stopPropagation(); lbPrev(); }} style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(60,60,60,0.8)', border: 'none', borderRadius: '50%',
              width: 40, height: 40, fontSize: 22, color: '#FFCC00', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, fontWeight: 700,
            }}>‹</button>
          )}

          {/* Right arrow */}
          {lightbox.images.length > 1 && lightbox.index < lightbox.images.length - 1 && (
            <button onClick={e => { e.stopPropagation(); lbNext(); }} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(60,60,60,0.8)', border: 'none', borderRadius: '50%',
              width: 40, height: 40, fontSize: 22, color: '#FFCC00', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, fontWeight: 700,
            }}>›</button>
          )}

          {/* Image */}
          <div onClick={e => e.stopPropagation()} style={{ overflow: 'auto', maxWidth: '100%', maxHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={lightbox.images[lightbox.index]} alt="" style={{
              transform: `scale(${lbZoom})`, transformOrigin: 'center center',
              maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8,
              transition: 'transform 0.1s ease',
            }} />
          </div>

          {/* Bottom controls */}
          <div onClick={e => e.stopPropagation()} style={{
            position: 'absolute', bottom: 24, display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <button onClick={() => setLbZoom(z => Math.max(0.5, z - 0.3))} style={lbBtnStyle}>−</button>
            <button onClick={() => setLbZoom(1)} style={{ ...lbBtnStyle, fontSize: 12, minWidth: 44 }}>{Math.round(lbZoom * 100)}%</button>
            <button onClick={() => setLbZoom(z => Math.min(5, z + 0.3))} style={lbBtnStyle}>+</button>
            <button onClick={() => { const src = lightbox.images[lightbox.index]; const url = new URL(src, window.location.origin).href; const fileName = src.split('/').pop() || 'photo.jpg'; if (window.Telegram?.WebApp?.downloadFile) { window.Telegram.WebApp.downloadFile({ url, file_name: fileName }); } else { window.open(url, '_blank'); } }} style={{ ...lbBtnStyle, padding: '10px 20px' }}>Скачать</button>
          </div>
        </div>
      )}
    </div>
  );
}

const askBtnStyle = (accent) => ({
  flex: 1, background: 'var(--bg-card)', borderRadius: 12, padding: '14px 10px',
  border: '1px solid var(--border)', cursor: 'pointer', textAlign: 'center',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
  borderBottom: `3px solid ${accent}`,
});

const filterChip = (active) => ({
  fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 8,
  background: active ? 'var(--accent-dim)' : 'var(--bg-card)',
  color: active ? 'var(--accent)' : 'var(--text-secondary)',
  border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
});

const tagStyle = (bg, color) => ({
  fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
  background: bg, color: color,
});

const backBtn = {
  background: 'none', border: 'none', color: 'var(--accent)',
  fontSize: 15, fontWeight: 500, cursor: 'pointer', padding: '4px 0', marginBottom: 12,
  fontFamily: 'inherit',
};

const textareaStyle = {
  width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)',
  borderRadius: 12, padding: '12px 14px', fontSize: 15, color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', resize: 'vertical',
};

const submitBtn = {
  width: '100%', background: 'var(--accent)', color: '#0D0D0D', border: 'none',
  borderRadius: 12, padding: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 16,
};

const attachBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
  padding: '8px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
  cursor: 'pointer', marginTop: 8,
};

const fileChipStyle = {
  fontSize: 12, padding: '4px 10px', borderRadius: 8,
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  color: 'var(--text-secondary)', textDecoration: 'none',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};

const lbBtnStyle = {
  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
  padding: '10px 14px', fontSize: 18, color: '#fff', fontWeight: 600, cursor: 'pointer',
};
